import React, { useState } from "react";
import { PlusIcon } from "@heroicons/react/24/outline";
import { Badge } from "../../../ui/Badge";
import { Button } from "../../../ui/Button";
import { Input } from "../../../ui/Input";
import { filterEvaluators } from "../utils/search";
import { SCORE_TYPE_LABEL, scoreTypeFor, splitCriteria } from "../utils/criteria";
import type { Evaluator } from "../types";

interface EvaluatorsListProps {
  evaluators: Evaluator[];
  selectedGroupId?: string;
  onSelect: (groupId: string) => void;
  onStartCreate: () => void;
}

export function EvaluatorsList({
  evaluators,
  selectedGroupId,
  onSelect,
  onStartCreate,
}: EvaluatorsListProps) {
  const [search, setSearch] = useState("");

  // Matches the criteria text as well as the generated name (see utils/search).
  const filtered = filterEvaluators(evaluators, search);

  return (
    <div className="w-72 shrink-0 border-r border-border flex flex-col h-full">
      <div className="p-3 border-b border-border">
        <Input
          type="search"
          className="h-8"
          placeholder="Search name or criteria…"
          aria-label="Search evaluators"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      <div className="flex-1 overflow-y-auto">
        {filtered.length === 0 && (
          <div className="p-4 text-sm text-muted-foreground text-center">
            {search ? "No evaluators match your search." : "No evaluators yet."}
          </div>
        )}
        {filtered.map((evaluator) => {
          const isSelected = evaluator.group_id === selectedGroupId;
          const { prefix } = splitCriteria(evaluator.criteria);
          const scoreType = prefix ? scoreTypeFor(prefix) : null;
          return (
            <button
              key={evaluator.id}
              onClick={() => onSelect(evaluator.group_id)}
              aria-current={isSelected ? "true" : undefined}
              className={[
                "w-full text-left px-4 py-3 border-b border-border hover:bg-accent transition-colors",
                "flex flex-col gap-1",
                isSelected
                  ? "border-l-4 border-l-primary bg-accent"
                  : "border-l-4 border-l-transparent",
              ].join(" ")}
            >
              <span className="font-bold text-sm truncate">{evaluator.group_id}</span>
              {/* One truncation, not two: `line-clamp-2` alone, so long criteria end in
                  an ellipsis instead of a hard 100-character cut with clamping on top. */}
              <span className="text-xs text-muted-foreground line-clamp-2">
                {evaluator.criteria}
              </span>
              {scoreType && (
                <Badge variant="outline" className="mt-0.5 self-start">
                  {SCORE_TYPE_LABEL[scoreType]}
                </Badge>
              )}
            </button>
          );
        })}
      </div>

      <div className="p-3 border-t border-border">
        {/* "New" opens the form; "Create evaluator" inside the form commits it. Two
            buttons with the same name on screen at once is a real ambiguity. */}
        <Button variant="primary" size="sm" className="w-full" onClick={onStartCreate}>
          <PlusIcon className="w-4 h-4" />
          New evaluator
        </Button>
      </div>
    </div>
  );
}
